import "../css/header.scss";
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import { Icon } from "./Icons";
import siteConfig from "../config/siteConfig";
import productData from "../Datas/productData";
import BrandLogo from "./BrandLogo";

const MEGA_MENUS = [
  {
    id: "phones",
    label: "Phones",
    path: "/shop?category=Phones",
    keywords: ["phone", "mobile", "magnetic"],
    groups: [
      {
        title: "Stands & wallets",
        links: [
          ["Snap-on stands", "/shop?category=Phones&type=Stand"],
          ["Tripod stands", "/shop?category=Phones&type=Tripod"],
          ["Everyday wallets", "/shop?category=Phones&type=Wallet"],
        ],
      },
      {
        title: "Cases & protection",
        links: [
          ["Phone cases", "/shop?category=Phones&type=Case"],
          ["Camera protection", "/shop?category=Phones&type=Protection"],
          ["Magnetic accessories", "/shop?category=Phones&type=Magnetic"],
        ],
      },
      {
        title: "Explore",
        links: [
          ["New arrivals", "/shop?category=Phones&sort=newest"],
          ["Best sellers", "/shop?category=Phones&sort=featured"],
          ["Shop all phone gear", "/shop?category=Phones"],
        ],
      },
    ],
  },
  {
    id: "tablets",
    label: "Tablets",
    path: "/shop?category=Tablets",
    keywords: ["tablet", "folio"],
    groups: [
      {
        title: "Cases",
        links: [
          ["Dynamic folios", "/shop?category=Tablets&type=Folio"],
          ["Protective cases", "/shop?category=Tablets&type=Case"],
          ["Connected covers", "/shop?category=Tablets&type=Cover"],
        ],
      },
      {
        title: "Stands",
        links: [
          ["Floating stands", "/shop?category=Tablets&type=Stand"],
          ["Snap-on stands", "/shop?category=Tablets&type=Magnetic"],
          ["Portable setups", "/shop?category=Tablets&type=Portable"],
        ],
      },
      {
        title: "Explore",
        links: [
          ["Creative essentials", "/shop?category=Tablets&tag=Creative"],
          ["New arrivals", "/shop?category=Tablets&sort=newest"],
          ["Shop all tablet gear", "/shop?category=Tablets"],
        ],
      },
    ],
  },
  {
    id: "laptops",
    label: "Laptops",
    path: "/shop?category=Laptops",
    keywords: ["laptop", "notebook", "sleeve"],
    groups: [
      {
        title: "Stands",
        links: [
          ["Invisible stands", "/shop?category=Laptops&type=Stand"],
          ["Sit-stand desks", "/shop?category=Laptops&type=Desk"],
          ["Travel stands", "/shop?category=Laptops&type=Portable"],
        ],
      },
      {
        title: "Carry",
        links: [
          ["Laptop sleeves", "/shop?category=Laptops&type=Sleeve"],
          ["Organizers", "/shop?category=Laptops&type=Organizer"],
          ["Everyday carry", "/shop?category=Carry"],
        ],
      },
      {
        title: "Explore",
        links: [
          ["Hybrid work", "/shop?category=Laptops&tag=Hybrid"],
          ["New arrivals", "/shop?category=Laptops&sort=newest"],
          ["Shop all laptop gear", "/shop?category=Laptops"],
        ],
      },
    ],
  },
  {
    id: "workspace",
    label: "Workspace",
    path: "/shop?category=Workspace",
    keywords: ["workspace", "desk", "office", "charger", "organizer"],
    groups: [
      {
        title: "Desk essentials",
        links: [
          ["Desktop stands", "/shop?category=Workspace&type=Stand"],
          ["Desk mats", "/shop?category=Workspace&type=Mat"],
          ["Cable organization", "/shop?category=Workspace&type=Organizer"],
        ],
      },
      {
        title: "Power & focus",
        links: [
          ["Charging", "/shop?category=Workspace&type=Charging"],
          ["Focus tools", "/shop?category=Workspace&type=Focus"],
          ["Home office", "/shop?category=Workspace&tag=Home%20Office"],
        ],
      },
      {
        title: "Explore",
        links: [
          ["Work from anywhere", "/shop?tag=Portable"],
          ["New arrivals", "/shop?category=Workspace&sort=newest"],
          ["Shop all workspace", "/shop?category=Workspace"],
        ],
      },
    ],
  },
  {
    id: "more",
    label: "More",
    path: "/about",
    keywords: [],
    groups: [
      {
        title: "Help",
        links: [
          ["FAQs", "/faqs"],
          ["Contact us", "/contact"],
          ["Shipping & delivery", "/shipping"],
          ["Returns", "/returns"],
        ],
      },
      {
        title: "Policies",
        links: [
          ["Privacy policy", "/privacy"],
          ["Cookie policy", "/cookies"],
          ["Terms of service", "/terms"],
        ],
      },
      {
        title: "Mendoriah",
        links: [
          ["Our story", "/about"],
          ["Email support", "/contact"],
        ],
      },
    ],
  },
];

const getCurrencyCode = () =>
  typeof siteConfig.currency === "string"
    ? siteConfig.currency
    : siteConfig.currency?.code || "USD";

const getProductText = (product) =>
  [
    product?.name,
    product?.category,
    product?.subcategory,
    product?.material,
    ...(Array.isArray(product?.tags) ? product.tags : []),
    ...(Array.isArray(product?.description)
      ? product.description
      : [product?.description]),
    product?.attributes ? JSON.stringify(product.attributes) : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const formatPrice = (price) => {
  const numericPrice = Number(
    typeof price === "string" ? price.replace(/[^0-9.-]/g, "") : price
  );

  if (!Number.isFinite(numericPrice)) return "";

  try {
    return new Intl.NumberFormat(siteConfig.locale || "en-US", {
      style: "currency",
      currency: getCurrencyCode(),
    }).format(numericPrice);
  } catch {
    return `$${numericPrice.toFixed(2)}`;
  }
};

const getAnnouncementText = (announcement) => {
  if (typeof announcement === "string") return announcement;
  return announcement?.text || announcement?.message || announcement?.label || "";
};

function Header() {
  const cart = useContext(CartContext) || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [announcementIndex, setAnnouncementIndex] = useState(0);
  const [activeMenu, setActiveMenu] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef(null);
  const searchDialogRef = useRef(null);
  const mobileDialogRef = useRef(null);
  const searchTriggerRef = useRef(null);
  const searchReturnFocusRef = useRef(null);
  const menuTriggerRef = useRef(null);

  const brandName = siteConfig.wordmark || siteConfig.brandName;
  const currencyCode = getCurrencyCode();
  const localeLabel = String(siteConfig.locale || "en-US")
    .split("-")[0]
    .toUpperCase();
  const cartCount = Number(cart.cartCount) || 0;
  const wishlistCount = Array.isArray(cart.wishlist) ? cart.wishlist.length : 0;

  const headerMenus = useMemo(() => {
    const configuredNavigation = Array.isArray(siteConfig.navigation)
      ? siteConfig.navigation
      : [];

    return MEGA_MENUS.map((menu) => {
      const configured = configuredNavigation.find(
        (item) =>
          String(item?.id || "").toLowerCase() === menu.id ||
          String(item?.label || "").toLowerCase() === menu.label.toLowerCase()
      );
      return configured
        ? {
            ...menu,
            label: configured.label || menu.label,
            path: configured.path || configured.href || menu.path,
          }
        : menu;
    });
  }, []);

  const announcements = useMemo(() => {
    const configured =
      siteConfig.announcements ||
      siteConfig.announcementBar?.messages ||
      siteConfig.promotionMessages;
    const freeShipping = siteConfig.shipping?.freeThreshold;
    const fallback = [
      freeShipping
        ? `Complimentary shipping on orders over $${freeShipping}`
        : "Complimentary shipping on qualifying orders",
      siteConfig.tagline || "Designed for movement. Made for every setup.",
      `${siteConfig.shipping?.returnsWindowDays || 30}-day easy returns`,
    ];
    const values = Array.isArray(configured) && configured.length
      ? configured.map(getAnnouncementText).filter(Boolean)
      : fallback;
    return values.length ? values : fallback;
  }, []);

  const featuredByMenu = useMemo(() => {
    const products = Array.isArray(productData) ? productData : [];
    return headerMenus.reduce((result, menu, menuIndex) => {
      const matched = products.filter((product) => {
        const searchText = getProductText(product);
        return menu.keywords.some((keyword) => searchText.includes(keyword));
      });
      const fallbackStart = products.length
        ? (menuIndex * 3) % products.length
        : 0;
      const fallbacks = products
        .slice(fallbackStart)
        .concat(products.slice(0, fallbackStart));
      result[menu.id] = (matched.length ? matched : fallbacks).slice(0, 3);
      return result;
    }, {});
  }, [headerMenus]);

  const searchResults = useMemo(() => {
    const products = Array.isArray(productData) ? productData : [];
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return products.slice(0, 6);

    const terms = normalizedQuery.split(/\s+/).filter(Boolean);
    return products
      .filter((product) => {
        const searchText = getProductText(product);
        return terms.every((term) => searchText.includes(term));
      })
      .slice(0, 6);
  }, [query]);

  useEffect(() => {
    if (announcements.length < 2) return undefined;
    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")
      .matches;
    if (reducedMotion) return undefined;

    const interval = window.setInterval(() => {
      setAnnouncementIndex((current) => (current + 1) % announcements.length);
    }, 5200);

    return () => window.clearInterval(interval);
  }, [announcements.length]);

  useEffect(() => {
    if (!isSearchOpen && !isMobileOpen && !activeMenu) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeMenu, isMobileOpen, isSearchOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;

      if (isSearchOpen) {
        setIsSearchOpen(false);
        window.requestAnimationFrame(() => {
          const previousTrigger = searchReturnFocusRef.current;
          if (previousTrigger?.offsetParent !== null) {
            previousTrigger.focus();
          } else {
            menuTriggerRef.current?.focus();
          }
        });
      } else if (isMobileOpen) {
        setIsMobileOpen(false);
        window.requestAnimationFrame(() => menuTriggerRef.current?.focus());
      } else if (activeMenu) {
        const menuId = activeMenu;
        setActiveMenu(null);
        window.requestAnimationFrame(() => {
          document
            .querySelector(`button[aria-controls="mega-${menuId}"]`)
            ?.focus();
        });
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [activeMenu, isMobileOpen, isSearchOpen]);

  useEffect(() => {
    if (isSearchOpen) {
      window.requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (isMobileOpen) {
      window.requestAnimationFrame(() => {
        mobileDialogRef.current?.querySelector("button")?.focus();
      });
    }
  }, [isMobileOpen]);

  useEffect(() => {
    setActiveMenu(null);
    setIsMobileOpen(false);
    setIsSearchOpen(false);
  }, [location.pathname, location.search]);

  const trapFocus = (event, containerRef) => {
    if (event.key !== "Tab") return;
    const focusable = Array.from(
      containerRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) || []
    ).filter(
      (element) =>
        !element.hasAttribute("hidden") &&
        window.getComputedStyle(element).visibility !== "hidden" &&
        element.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const openSearch = () => {
    searchReturnFocusRef.current = document.activeElement;
    setActiveMenu(null);
    setIsMobileOpen(false);
    setIsSearchOpen(true);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    window.requestAnimationFrame(() => {
      const previousTrigger = searchReturnFocusRef.current;
      if (previousTrigger?.offsetParent !== null) {
        previousTrigger.focus();
      } else {
        menuTriggerRef.current?.focus();
      }
    });
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const value = query.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/shop");
    setIsSearchOpen(false);
  };

  const openMegaMenuFromKeyboard = (event, menuId) => {
    if (event.key !== "ArrowDown") return;
    event.preventDefault();
    setActiveMenu(menuId);
    window.requestAnimationFrame(() => {
      document.querySelector(`#mega-${menuId} a`)?.focus();
    });
  };

  return (
    <header
      className={`site-header${activeMenu ? " has-open-menu" : ""}`}
    >
      <div className="announcement-bar" aria-label="Store announcements">
        <p aria-live="polite" key={announcementIndex}>
          {announcements[announcementIndex % announcements.length]}
        </p>
        {announcements.length > 1 && (
          <div className="announcement-bar__dots" aria-hidden="true">
            {announcements.map((announcement, index) => (
              <span
                className={index === announcementIndex ? "is-active" : ""}
                key={`${announcement}-${index}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="site-header__main">
        <div className="site-header__inner">
          <button
            aria-controls="mobile-navigation"
            aria-expanded={isMobileOpen}
            aria-label="Open navigation"
            className="header-action header-action--menu"
            onClick={() => {
              setActiveMenu(null);
              setIsMobileOpen(true);
            }}
            ref={menuTriggerRef}
            type="button"
          >
            <Icon name="menu" size={23} />
          </button>

          <Link className="site-header__logo" to="/" aria-label={`${brandName} home`}>
            <BrandLogo />
          </Link>

          <nav
            aria-label="Primary navigation"
            className="header-nav"
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setActiveMenu(null);
              }
            }}
            onMouseLeave={() => setActiveMenu(null)}
          >
            <ul>
              {headerMenus.map((menu) => {
                const isOpen = activeMenu === menu.id;
                return (
                  <li
                    className="header-nav__item"
                    key={menu.id}
                    onMouseEnter={() => setActiveMenu(menu.id)}
                  >
                    <button
                      aria-controls={`mega-${menu.id}`}
                      aria-expanded={isOpen}
                      className={isOpen ? "is-active" : ""}
                      onClick={() => setActiveMenu(isOpen ? null : menu.id)}
                      onKeyDown={(event) =>
                        openMegaMenuFromKeyboard(event, menu.id)
                      }
                      type="button"
                    >
                      {menu.label}
                      <Icon name="chevron" size={13} />
                    </button>

                    <div
                      aria-hidden={!isOpen}
                      className={`header-mega${isOpen ? " is-open" : ""}`}
                      id={`mega-${menu.id}`}
                    >
                      <div className="header-mega__inner">
                        <div className="header-mega__links">
                          {menu.groups.map((group) => (
                            <section key={group.title}>
                              <h2>{group.title}</h2>
                              <ul>
                                {group.links.map(([label, path]) => (
                                  <li key={label}>
                                    <Link to={path}>{label}</Link>
                                  </li>
                                ))}
                              </ul>
                            </section>
                          ))}
                          <Link className="header-mega__all" to={menu.path}>
                            View all {menu.label.toLowerCase()}
                            <Icon name="arrow" size={16} />
                          </Link>
                        </div>

                        {menu.id !== "more" && <div className="header-mega__products" aria-label={`Featured ${menu.label}`}>
                          {(featuredByMenu[menu.id] || []).map((product) => (
                            <Link
                              className="header-mega-card"
                              key={product.id}
                              to={`/shopDetails/${product.id}`}
                            >
                              <span className="header-mega-card__media">
                                {(product.images?.[0] || product.image) && (
                                  <img
                                    alt=""
                                    loading="lazy"
                                    src={product.images?.[0] || product.image}
                                  />
                                )}
                              </span>
                              <span className="header-mega-card__name">
                                {product.name}
                              </span>
                            </Link>
                          ))}
                        </div>}
                      </div>
                    </div>
                  </li>
                );
              })}
              <li className="header-nav__item header-nav__item--link">
                <Link to="/shop">Shop All</Link>
              </li>
            </ul>
          </nav>

          <div className="site-header__actions">
            <span className="header-locale" aria-label={`Language ${localeLabel}, currency ${currencyCode}`}>
              <Icon name="globe" size={17} />
              {localeLabel} / {currencyCode}
            </span>
            <button
              aria-label="Search products"
              className="header-action header-action--search"
              onClick={openSearch}
              ref={searchTriggerRef}
              type="button"
            >
              <Icon name="search" size={21} />
            </button>
            <Link
              aria-label="Account"
              className="header-action header-action--account"
              to="/account"
            >
              <Icon name="user" size={21} />
            </Link>
            <Link
              aria-label={`Wishlist, ${wishlistCount} ${wishlistCount === 1 ? "item" : "items"}`}
              className="header-action header-action--wishlist"
              to="/wishlist"
            >
              <Icon name="heart" size={21} />
              {wishlistCount > 0 && (
                <span className="header-action__count">
                  {wishlistCount > 99 ? "99+" : wishlistCount}
                </span>
              )}
            </Link>
            <Link
              aria-label={`Shopping bag, ${cartCount} ${cartCount === 1 ? "item" : "items"}`}
              className="header-action"
              to="/cartpage"
            >
              <Icon name="bag" size={22} />
              {cartCount > 0 && (
                <span className="header-action__count">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>

      {activeMenu && (
        <button
          aria-label="Close navigation menu"
          className="header-mega-backdrop"
          onClick={() => setActiveMenu(null)}
          tabIndex="-1"
          type="button"
        />
      )}

      <div
        aria-hidden={!isMobileOpen}
        className={`mobile-nav-overlay${isMobileOpen ? " is-open" : ""}`}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) setIsMobileOpen(false);
        }}
      >
        <aside
          aria-label="Mobile navigation"
          aria-modal="true"
          className="mobile-nav"
          id="mobile-navigation"
          onKeyDown={(event) => trapFocus(event, mobileDialogRef)}
          ref={mobileDialogRef}
          role="dialog"
        >
          <div className="mobile-nav__header">
            <span className="mobile-nav__wordmark"><BrandLogo /></span>
            <button
              aria-label="Close navigation"
              className="header-action"
              onClick={() => {
                setIsMobileOpen(false);
                window.requestAnimationFrame(() => menuTriggerRef.current?.focus());
              }}
              type="button"
            >
              <Icon name="close" size={23} />
            </button>
          </div>

          <button className="mobile-nav__search" onClick={openSearch} type="button">
            <Icon name="search" size={19} />
            Search products
          </button>

          <div className="mobile-nav__sections">
            {headerMenus.map((menu) => {
              const isExpanded = mobileSection === menu.id;
              return (
                <section className="mobile-nav__section" key={menu.id}>
                  <button
                    aria-controls={`mobile-${menu.id}`}
                    aria-expanded={isExpanded}
                    onClick={() =>
                      setMobileSection(isExpanded ? null : menu.id)
                    }
                    type="button"
                  >
                    {menu.label}
                    <Icon name="chevron" size={16} />
                  </button>
                  <div
                    className={`mobile-nav__submenu${isExpanded ? " is-open" : ""}`}
                    id={`mobile-${menu.id}`}
                  >
                    {menu.groups.map((group) => (
                      <div key={group.title}>
                        <h2>{group.title}</h2>
                        {group.links.map(([label, path]) => (
                          <Link key={label} to={path}>
                            {label}
                          </Link>
                        ))}
                      </div>
                    ))}
                    <Link className="mobile-nav__view-all" to={menu.path}>
                      View all {menu.label.toLowerCase()}
                      <Icon name="arrow" size={15} />
                    </Link>
                  </div>
                </section>
              );
            })}
            <Link className="mobile-nav__direct-link" to="/shop">
              Shop All
              <Icon name="arrow" size={17} />
            </Link>
            <Link className="mobile-nav__direct-link" to="/about">
              Our Story
              <Icon name="arrow" size={17} />
            </Link>
            <Link className="mobile-nav__direct-link" to="/wishlist">
              Saved items {wishlistCount > 0 ? `(${wishlistCount})` : ""}
              <Icon name="arrow" size={17} />
            </Link>
            <Link className="mobile-nav__direct-link" to="/account">
              Account
              <Icon name="arrow" size={17} />
            </Link>
          </div>

          <div className="mobile-nav__footer">
            <span><Icon name="globe" size={17} /> {localeLabel} / {currencyCode}</span>
            <Link to="/contact">Need help?</Link>
          </div>
        </aside>
      </div>

      <div
        aria-hidden={!isSearchOpen}
        className={`search-drawer-overlay${isSearchOpen ? " is-open" : ""}`}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeSearch();
        }}
      >
        <aside
          aria-labelledby="search-drawer-title"
          aria-modal="true"
          className="search-drawer"
          onKeyDown={(event) => trapFocus(event, searchDialogRef)}
          ref={searchDialogRef}
          role="dialog"
        >
          <div className="search-drawer__header">
            <div>
              <span className="search-drawer__eyebrow">Find your setup</span>
              <h2 id="search-drawer-title">Search products</h2>
            </div>
            <button
              aria-label="Close search"
              className="header-action"
              onClick={closeSearch}
              type="button"
            >
              <Icon name="close" size={23} />
            </button>
          </div>

          <form className="search-drawer__form" onSubmit={submitSearch} role="search">
            <Icon name="search" size={22} />
            <label className="sr-only" htmlFor="site-search-input">
              Search the product catalog
            </label>
            <input
              autoComplete="off"
              id="site-search-input"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search by product, device or feature"
              ref={searchInputRef}
              type="search"
              value={query}
            />
            {query && (
              <button
                aria-label="Clear search"
                className="search-drawer__clear"
                onClick={() => {
                  setQuery("");
                  searchInputRef.current?.focus();
                }}
                type="button"
              >
                Clear
              </button>
            )}
          </form>

          <div className="search-drawer__results" aria-live="polite">
            <div className="search-drawer__result-heading">
              <span>{query.trim() ? "Search results" : "Popular picks"}</span>
              <small>{searchResults.length} shown</small>
            </div>
            {searchResults.length > 0 ? (
              <ul>
                {searchResults.map((product) => (
                  <li key={product.id}>
                    <Link to={`/shopDetails/${product.id}`}>
                      <span className="search-result__media">
                        {(product.images?.[0] || product.image) && (
                          <img
                            alt=""
                            loading="lazy"
                            src={product.images?.[0] || product.image}
                          />
                        )}
                      </span>
                      <span className="search-result__copy">
                        <strong>{product.name}</strong>
                        <span>{product.category || "Everyday accessories"}</span>
                      </span>
                      <span className="search-result__price">
                        {formatPrice(product.price)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="search-drawer__empty">
                <span>No products found</span>
                <p>Try a device name, material, color or product feature.</p>
              </div>
            )}
          </div>

          <Link
            className="search-drawer__all"
            to={query.trim() ? `/search?q=${encodeURIComponent(query.trim())}` : "/shop"}
          >
            {query.trim() ? `View all results for “${query.trim()}”` : "Explore all products"}
            <Icon name="arrow" size={17} />
          </Link>
        </aside>
      </div>
    </header>
  );
}

export default Header;
